import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { usersApi } from 'api/endpoints/users.api';
import { applicationsApi } from 'api/endpoints/applications.api';
import { pushApi } from 'api/endpoints/push.api';
import { UserRole, RegisterRequest } from 'types/user.types';
import { Segment } from 'types/push.types';
import { ApplicationLookup } from 'types/application.types';
import { useAuth } from 'hooks/useAuth';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import styles from './UserForm.module.css';

interface UserFormData {
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
  buyerId: string;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  buyerId?: string;
}

interface SegmentWithApp extends Segment {
  appName?: string;
}

export const UserForm: React.FC = () => {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canManageUsers } = useAuth();
  const isEditMode = !!userId;
  const [loading, setLoading] = useState(false);
  const [loadingApps, setLoadingApps] = useState(false);
  const [loadingSegments, setLoadingSegments] = useState(false);

  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    role: UserRole.VIEWER,
    buyerId: '',
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [applications, setApplications] = useState<ApplicationLookup[]>([]);
  const [selectedApps, setSelectedApps] = useState<number[]>([]);
  const [appSearch, setAppSearch] = useState('');
  const [segments, setSegments] = useState<SegmentWithApp[]>([]);
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);
  const [segmentSearch, setSegmentSearch] = useState('');


  useEffect(() => {
    if (!canManageUsers) {
      navigate(ROUTES.DASHBOARD);
      notify.error('У вас нет прав для управления пользователями');
      return;
    }
    loadApplications();
    if (isEditMode) loadUser();
  }, [userId, canManageUsers]);

  useEffect(() => {
    if (formData.role === UserRole.ACC_MANAGER && segments.length === 0) {
      loadAllSegments();
    }
  }, [formData.role]);

  const filteredApps = useMemo(() => {
    if (!appSearch) return applications;
    const q = appSearch.toLowerCase();
    return applications.filter(
      a => a.name.toLowerCase().includes(q) || a.bundle_id.toLowerCase().includes(q)
    );
  }, [applications, appSearch]);


  const filteredSegments = useMemo(() => {
    if (!segmentSearch) return segments;
    const q = segmentSearch.toLowerCase();
    return segments.filter(
      s => s.name.toLowerCase().includes(q) || (s.appName ?? '').toLowerCase().includes(q)
    );
  }, [segments, segmentSearch]);

  const loadApplications = async () => {
    setLoadingApps(true);
    try {
      const resp = await applicationsApi.lookup();
      setApplications(resp.applications || []);
    } catch {
      notify.error('Не удалось загрузить приложения');
    } finally {
      setLoadingApps(false);
    }
  };

  const loadAllSegments = async () => {
    setLoadingSegments(true);
    try {
      const apps = applications.length > 0
        ? applications
        : await applicationsApi.lookup().then(r => r.applications || []);

      const results = await Promise.all(
        apps.map(async app => {
          try {
            const resp = await pushApi.listSegments(app.id, 1000, 0);
            return (resp.segments || []).map((s: Segment) => ({ ...s, appName: app.name }));
          } catch {
            return [] as SegmentWithApp[];
          }
        })
      );
      setSegments(results.flat());
    } catch {
      notify.error('Не удалось загрузить сегменты');
    } finally {
      setLoadingSegments(false);
    }
  };

  const loadUser = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const user = await usersApi.get(parseInt(userId));
      setFormData({
        email: user.email,
        password: '',
        confirmPassword: '',
        role: user.role,
        buyerId: user.buyer_id ?? '',
      });

      if (user.role === UserRole.VIEWER || user.role === UserRole.BUYER) {
        const userApps = await usersApi.getUserApplications(parseInt(userId));
        setSelectedApps(userApps);
      }
      if (user.role === UserRole.ACC_MANAGER && user.segment_ids?.length) {
        setSelectedSegmentIds(user.segment_ids);
      }
    } catch (error: any) {
      notify.error(error.response?.data?.error || 'Не удалось загрузить пользователя');
      navigate(ROUTES.USERS);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    if (!formData.email) newErrors.email = 'Email обязателен';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Неверный формат';
    if (!isEditMode) {
      if (!formData.password || formData.password.length < 8)
        newErrors.password = 'Минимум 8 символов';
      if (formData.password !== formData.confirmPassword)
        newErrors.confirmPassword = 'Пароли не совпадают';
    }
    if (formData.role === UserRole.BUYER && !formData.buyerId.trim())
      newErrors.buyerId = 'Укажите ID байера';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      let uid: number;
            if (isEditMode) {
        uid = parseInt(userId!);
        await usersApi.update(uid, {
          email: formData.email,
          role: formData.role,
          buyer_id: formData.role === UserRole.BUYER ? formData.buyerId.trim() : undefined,
        });
        notify.success('Пользователь обновлён');
      } else {
        const request: RegisterRequest = {
          email: formData.email,
          password: formData.password,
          role: formData.role === UserRole.BUYER ? UserRole.VIEWER : formData.role,
        };
        const response = await usersApi.create(request);
        uid = response.user?.id || 0;
        if (formData.role === UserRole.BUYER && uid > 0) {
          await usersApi.changeRole(uid, UserRole.BUYER, formData.buyerId.trim());
        }
        notify.success('Пользователь создан');
      }

      if (uid > 0) {
        if (formData.role === UserRole.VIEWER || formData.role === UserRole.BUYER) await updateApplicationAccess(uid);
        if (formData.role === UserRole.ACC_MANAGER) await updateSegments(uid);
      }

      navigate(ROUTES.USERS);
    } catch (error: any) {
      notify.error(error.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationAccess = async (uid: number) => {
    try {
      const currentApps = isEditMode ? await usersApi.getUserApplications(uid) : [];
      const toGrant = selectedApps.filter(id => !currentApps.includes(id));
      const toRevoke = currentApps.filter(id => !selectedApps.includes(id));
      await Promise.all([
        ...toGrant.map(appId => applicationsApi.grantAccess({ viewer_id: uid, app_id: appId })),
        ...toRevoke.map(appId => applicationsApi.revokeAccess({ viewer_id: uid, app_id: appId })),
      ]);
    } catch {
      notify.warning('Пользователь сохранён, но права доступа могли обновиться не полностью');
    }
  };

  const updateSegments = async (uid: number) => {
    try {
      await usersApi.setSegments(uid, selectedSegmentIds);
    } catch {
      notify.warning('Пользователь сохранён, но сегменты не удалось привязать');
    }
  };

  const handleRoleChange = (newRole: UserRole) => {
    setFormData(prev => ({
      ...prev,
      role: newRole,
      buyerId: newRole === UserRole.BUYER ? prev.buyerId : '',
    }));
    if (newRole !== UserRole.VIEWER && newRole !== UserRole.BUYER) setSelectedApps([]);
    if (newRole !== UserRole.ACC_MANAGER) setSelectedSegmentIds([]);
  };

  const handleAppToggle = (appId: number) => {
    setSelectedApps(prev =>
      prev.includes(appId) ? prev.filter(id => id !== appId) : [...prev, appId]
    );
  };

  const handleSegmentToggle = (segId: number) => {
    setSelectedSegmentIds(prev =>
      prev.includes(segId) ? prev.filter(id => id !== segId) : [...prev, segId]
    );
  };

  const handleSelectFilteredApps = () => {
    const visibleIds = filteredApps.map(a => a.id);
    const allSelected = visibleIds.every(id => selectedApps.includes(id));
    if (allSelected) {
      setSelectedApps(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedApps(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleSelectFilteredSegments = () => {
    const visibleIds = filteredSegments.map(s => s.id);
    const allSelected = visibleIds.every(id => selectedSegmentIds.includes(id));
    if (allSelected) {
      setSelectedSegmentIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedSegmentIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  return (
    <div className={styles.userFormContainer}>
      <div className={styles.formHeader}>
        <h1>{isEditMode ? 'Редактирование' : 'Новый пользователь'}</h1>
        <button type="button" className={styles.btnClose} onClick={() => navigate(ROUTES.USERS)}>✕</button>
      </div>

      <form onSubmit={handleSubmit} className={styles.modernForm}>
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>Основные данные</h3>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                className={clsx(errors.email && styles.error)}
                placeholder="name@company.com"
                disabled={isEditMode}
              />
              {errors.email && <span className={styles.errorMsg}>{errors.email}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="role">Роль</label>
              <select
                id="role"
                value={formData.role}
                onChange={e => handleRoleChange(e.target.value as UserRole)}
              >
                <option value={UserRole.VIEWER}>Viewer — Наблюдатель</option>
                <option value={UserRole.EXECUTOR}>Executor — Исполнитель</option>
                <option value={UserRole.ADMIN}>Admin — Администратор</option>
                <option value={UserRole.ACC_MANAGER}>AccManager — Аккаунт-менеджер</option>
                <option value={UserRole.BUYER}>Buyer — Байер</option>
              </select>
            </div>
          </div>

          {formData.role === UserRole.BUYER && (
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="buyerId">ID байера</label>
                <input
                  type="text"
                  id="buyerId"
                  value={formData.buyerId}
                  onChange={e => setFormData({ ...formData, buyerId: e.target.value })}
                  className={clsx(errors.buyerId && styles.error)}
                  placeholder="Например: 5770"
                />
                {errors.buyerId && <span className={styles.errorMsg}>{errors.buyerId}</span>}
              </div>
            </div>
          )}

          {!isEditMode && (
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Пароль</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  className={clsx(errors.password && styles.error)}
                />
                {errors.password && <span className={styles.errorMsg}>{errors.password}</span>}
              </div>
              <div className={styles.formGroup}>
                <label>Подтверждение</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={clsx(errors.confirmPassword && styles.error)}
                />
                {errors.confirmPassword && <span className={styles.errorMsg}>{errors.confirmPassword}</span>}
              </div>
            </div>
          )}
        </div>
        {(formData.role === UserRole.VIEWER || formData.role === UserRole.BUYER) && (
          <div className={styles.formSection}>
            <div className={styles.accessHeader}>
              <h3 className={styles.sectionTitle}>Доступ к приложениям</h3>
              <div className={styles.accessStats}>
                Выбрано: <strong>{selectedApps.length}</strong>
              </div>
            </div>

            <div className={styles.searchBar}>
              <input
                type="text"
                placeholder="Поиск по названию или Bundle ID..."
                value={appSearch}
                onChange={e => setAppSearch(e.target.value)}
                className={styles.searchInput}
              />
              {applications.length > 0 && (
                <button type="button" className="btn-text" onClick={handleSelectFilteredApps}>
                  {filteredApps.every(a => selectedApps.includes(a.id)) && filteredApps.length > 0
                    ? 'Снять выбор с найденных'
                    : 'Выбрать найденные'}
                </button>
              )}
            </div>

            <div className={styles.appsGridContainer}>
              {loadingApps ? (
                <div className={styles.loadingState}>Загрузка приложений...</div>
              ) : filteredApps.length === 0 ? (
                <div className={styles.emptyState}>{appSearch ? 'Ничего не найдено' : 'Приложений нет'}</div>
              ) : (
                <div className={styles.appsGrid}>
                  {filteredApps.map(app => {
                    const isSelected = selectedApps.includes(app.id);
                    return (
                      <div
                        key={app.id}
                        className={clsx(styles.appCard, isSelected && styles.selected)}
                        onClick={() => handleAppToggle(app.id)}
                      >
                        <div className={styles.appCardCheck}>
                          <div className={styles.checkbox} />
                        </div>
                        <div className={styles.appCardInfo}>
                          <div className={styles.appName}>{app.name}</div>
                          <div className={styles.appBundle}>{app.bundle_id}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {formData.role === UserRole.ACC_MANAGER && (
          <div className={styles.formSection}>
            <div className={styles.accessHeader}>
              <h3 className={styles.sectionTitle}>Сегменты аккаунт-менеджера</h3>
              <div className={styles.accessStats}>
                Выбрано: <strong>{selectedSegmentIds.length}</strong>
                {selectedSegmentIds.length > 0 && (
                  <button
                    type="button"
                    className={clsx('btn-text', styles.btnTextDanger)}
                    style={{ marginLeft: 12 }}
                    onClick={() => setSelectedSegmentIds([])}
                  >
                    Снять все
                  </button>
                )}
              </div>
            </div>

            <p className={styles.sectionHint}>
              Аккаунт-менеджер будет видеть историю пушей по всем выбранным сегментам.
              Можно выбрать несколько сегментов из разных приложений.
            </p>

            <div className={styles.searchBar}>
              <input
                type="text"
                placeholder="Поиск по названию сегмента или приложению..."
                value={segmentSearch}
                onChange={e => setSegmentSearch(e.target.value)}
                className={styles.searchInput}
              />
              {filteredSegments.length > 0 && (
                <button type="button" className="btn-text" onClick={handleSelectFilteredSegments}>
                  {filteredSegments.every(s => selectedSegmentIds.includes(s.id)) && filteredSegments.length > 0
                    ? 'Снять выбор с найденных'
                    : 'Выбрать найденные'}
                </button>
              )}
            </div>

            <div className={styles.appsGridContainer}>
              {loadingSegments ? (
                <div className={styles.loadingState}>Загрузка сегментов...</div>
              ) : filteredSegments.length === 0 ? (
                <div className={styles.emptyState}>
                  {segmentSearch
                    ? 'Ничего не найдено'
                    : 'Сегментов нет. Сначала создайте сегменты в Push Center.'}
                </div>
              ) : (
                <div className={styles.appsGrid}>
                  {filteredSegments.map(seg => {
                    const isSelected = selectedSegmentIds.includes(seg.id);
                    return (
                      <div
                        key={seg.id}
                        className={clsx(styles.appCard, isSelected && styles.selected)}
                        onClick={() => handleSegmentToggle(seg.id)}
                      >
                        <div className={styles.appCardCheck}>
                          <div className={styles.checkbox} />
                        </div>
                        <div className={styles.appCardInfo}>
                          <div className={styles.appName}>{seg.name}</div>
                          <div className={styles.appBundle}>
                            {seg.appName && <span className={styles.appTag}>{seg.appName}</span>}
                            {seg.user_count !== undefined && (
                              <span className={styles.userCount}> · {seg.user_count.toLocaleString()} польз.</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.formActions}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(ROUTES.USERS)}>
            Отмена
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Сохранение...' : 'Сохранить пользователя'}
          </button>
        </div>
      </form>
    </div>
  );
};